import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Volume2, RotateCcw, CheckCircle2, X, ChevronRight, ArrowLeft, Lightbulb, Trophy, Target } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSounds } from '@/hooks/useSounds';
import { speakText } from '@/utils/speechUtils';

export default function SwipeVocabularyCard({ 
  cards, // [{front: 'Gate', back: 'Kapı', example: 'Gate 5 is now boarding.', phonetic: '/ɡeɪt/'}, ...]
  title,
  onAnswer,
  onComplete,
  nextLessonInfo,
  onNextLesson,
  onBackToUnit,
  disabled = false 
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [knownCards, setKnownCards] = useState([]);
  const [unknownCards, setUnknownCards] = useState([]);
  const [isCompleted, setIsCompleted] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState(null); // 'left' | 'right' | null
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  
  const cardRef = useRef(null);
  const { playCardFlip, playCorrect, playIncorrect, playLessonComplete, playClick } = useSounds();

  const currentCard = cards?.[currentIndex];
  const totalProcessed = knownCards.length + unknownCards.length;
  const progress = (totalProcessed / cards?.length) * 100 || 0;

  // Touch/Mouse event handlers for swipe
  const handleDragStart = (e) => {
    if (disabled || !isFlipped) return;
    setIsDragging(true);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setStartPos({ x: clientX, y: clientY });
  };

  const handleDragMove = (e) => {
    if (!isDragging || disabled) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const deltaX = clientX - startPos.x;
    const deltaY = (clientY - startPos.y) * 0.3; // Limit vertical movement
    setDragOffset({ x: deltaX, y: deltaY });
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    const threshold = 100; // Minimum swipe distance
    
    if (dragOffset.x > threshold) {
      // Swiped right - Known
      handleSwipe('right');
    } else if (dragOffset.x < -threshold) {
      // Swiped left - Unknown
      handleSwipe('left');
    } else {
      // Reset position
      setDragOffset({ x: 0, y: 0 });
    }
  };

  const handleSwipe = (direction) => {
    setSwipeDirection(direction);
    
    if (direction === 'right') {
      playCorrect();
      setKnownCards(prev => [...prev, currentIndex]);
    } else {
      playIncorrect();
      setUnknownCards(prev => [...prev, currentIndex]);
    }
    
    // Animate out then move to next
    setTimeout(() => {
      setSwipeDirection(null);
      setDragOffset({ x: 0, y: 0 });
      setIsFlipped(false);
      
      // Check if all cards processed
      const newTotal = knownCards.length + unknownCards.length + 1;
      if (newTotal >= cards.length) {
        setIsCompleted(true);
        playLessonComplete();
        const score = (knownCards.length + (direction === 'right' ? 1 : 0)) / cards.length;
        setTimeout(() => {
          onAnswer?.(score >= 0.7, score);
          if (onComplete) onComplete();
        }, 500);
      } else {
        setCurrentIndex(prev => prev + 1);
      }
    }, 300);
  };

  const handleFlip = () => {
    if (disabled || swipeDirection) return;
    playCardFlip();
    setIsFlipped(!isFlipped);
  };

  const handleReset = () => {
    setCurrentIndex(0);
    setIsFlipped(false);
    setKnownCards([]);
    setUnknownCards([]);
    setIsCompleted(false);
    setSwipeDirection(null);
    setDragOffset({ x: 0, y: 0 });
  };

  // Button handlers for non-touch devices
  const handleKnownClick = () => {
    if (!isFlipped || disabled) return;
    handleSwipe('right');
  };

  const handleUnknownClick = () => {
    if (!isFlipped || disabled) return;
    handleSwipe('left');
  };

  // speakText artık utils'ten import ediliyor - otomatik dil algılama yapıyor

  // Calculate card rotation based on drag
  const getCardStyle = () => {
    if (swipeDirection === 'right') {
      return {
        transform: 'translateX(150%) rotate(30deg)',
        opacity: 0,
        transition: 'all 0.3s ease-out'
      };
    }
    if (swipeDirection === 'left') {
      return {
        transform: 'translateX(-150%) rotate(-30deg)',
        opacity: 0,
        transition: 'all 0.3s ease-out'
      };
    }
    if (isDragging) {
      const rotation = dragOffset.x * 0.1;
      return {
        transform: `translateX(${dragOffset.x}px) translateY(${dragOffset.y}px) rotate(${rotation}deg)`,
        transition: 'none'
      };
    }
    return {
      transform: 'translateX(0) rotate(0)',
      transition: 'all 0.3s ease-out'
    };
  };

  // Show swipe indicators
  const getLeftIndicatorOpacity = () => Math.min(Math.abs(Math.min(dragOffset.x, 0)) / 100, 1);
  const getRightIndicatorOpacity = () => Math.min(Math.max(dragOffset.x, 0) / 100, 1);

  if (!cards || cards.length === 0) {
    return (
      <div className="text-center p-8">
        <p className="text-gray-500">Kart bulunamadı</p>
      </div>
    );
  }

  // Completion Screen
  if (isCompleted) {
    const accuracy = Math.round((knownCards.length / cards.length) * 100);
    const isPerfect = accuracy === 100;
    const isGood = accuracy >= 70;
    
    return (
      <div className="w-full max-w-lg mx-auto text-center">
        <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100">
          {/* Trophy/Icon */}
          <div className={cn(
            'w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6',
            isPerfect ? 'bg-gradient-to-br from-yellow-400 to-amber-500' :
            isGood ? 'bg-gradient-to-br from-green-400 to-emerald-500' :
            'bg-gradient-to-br from-orange-400 to-amber-500'
          )}>
            {isPerfect ? (
              <Trophy className="w-12 h-12 text-white" />
            ) : (
              <Target className="w-12 h-12 text-white" />
            )}
          </div>
          
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {isPerfect ? 'Mükemmel! 🌟' : isGood ? 'Harika! 🎉' : 'Devam Et! 💪'}
          </h2>
          <p className="text-gray-500 mb-6">
            {cards.length} kelimeden {knownCards.length} tanesini biliyorsun
          </p>
          
          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-4 border border-green-100">
              <div className="flex items-center justify-center gap-2 mb-1">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span className="text-3xl font-bold text-green-600">{knownCards.length}</span>
              </div>
              <p className="text-sm text-green-600 font-medium">Biliyorum</p>
            </div>
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-4 border border-red-100">
              <div className="flex items-center justify-center gap-2 mb-1">
                <X className="w-5 h-5 text-red-500" />
                <span className="text-3xl font-bold text-red-600">{unknownCards.length}</span>
              </div>
              <p className="text-sm text-red-600 font-medium">Bilmiyorum</p>
            </div>
          </div>

          {/* Accuracy Bar */}
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-500">Başarı Oranı</span>
              <span className={cn(
                'font-bold',
                isPerfect ? 'text-yellow-600' : isGood ? 'text-green-600' : 'text-orange-600'
              )}>%{accuracy}</span>
            </div>
            <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className={cn(
                  'h-full rounded-full transition-all duration-500',
                  isPerfect ? 'bg-gradient-to-r from-yellow-400 to-amber-500' :
                  isGood ? 'bg-gradient-to-r from-green-400 to-emerald-500' :
                  'bg-gradient-to-r from-orange-400 to-amber-500'
                )}
                style={{ width: `${accuracy}%` }}
              />
            </div>
          </div>

          {/* Next Lesson Info */}
          {nextLessonInfo && !nextLessonInfo.isLevelComplete && (
            <div className="bg-blue-50 rounded-xl p-4 mb-4 border border-blue-100">
              <p className="text-xs text-blue-600 font-medium mb-1">
                {nextLessonInfo.isNewUnit ? `📚 Yeni Ünite: ${nextLessonInfo.unitTitle}` : 'Sonraki Ders'}
              </p>
              <p className="text-sm font-bold text-blue-800">{nextLessonInfo.lesson?.title}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {onNextLesson && (
              <Button 
                onClick={onNextLesson} 
                className="w-full py-4 bg-emerald-500 hover:bg-emerald-600 rounded-xl"
              >
                {nextLessonInfo?.isLevelComplete 
                  ? 'Seviyeyi Tamamla 🏆' 
                  : nextLessonInfo?.isUnitComplete 
                    ? 'Sonraki Üniteye Geç' 
                    : 'Sonraki Derse Geç'
                }
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            )}
            
            {!onNextLesson && !onBackToUnit && onComplete && (
              <Button 
                onClick={onComplete} 
                className="w-full py-4 bg-emerald-500 hover:bg-emerald-600 rounded-xl"
              >
                Devam Et
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            )}

            {onBackToUnit && (
              <Button 
                onClick={onBackToUnit} 
                variant="outline" 
                className="w-full py-3 border-blue-300 text-blue-600 hover:bg-blue-50 rounded-xl"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Üniteye Dön
              </Button>
            )}

            {unknownCards.length > 0 && (
              <Button onClick={handleReset} variant="outline" className="w-full py-3 rounded-xl">
                <RotateCcw className="w-5 h-5 mr-2" />
                Bilmediklerimi Tekrar Et ({unknownCards.length})
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-lg mx-auto select-none">
      {/* Title & Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-bold text-gray-700 text-lg">{title || '📚 Kelime Kartları'}</h3>
          <span className="text-sm font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
            {currentIndex + 1} / {cards.length}
          </span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-blue-400 to-purple-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Swipe Instructions */}
      <div className="flex justify-between items-center mb-4 px-4">
        <div className={cn(
          'flex items-center gap-2 px-3 py-1.5 rounded-full transition-all',
          getLeftIndicatorOpacity() > 0.3 ? 'bg-red-100 scale-110' : 'bg-gray-100'
        )} style={{ opacity: 0.5 + getLeftIndicatorOpacity() * 0.5 }}>
          <X className="w-4 h-4 text-red-500" />
          <span className="text-xs font-medium text-red-600">Bilmiyorum</span>
        </div>
        <div className={cn(
          'flex items-center gap-2 px-3 py-1.5 rounded-full transition-all',
          getRightIndicatorOpacity() > 0.3 ? 'bg-green-100 scale-110' : 'bg-gray-100'
        )} style={{ opacity: 0.5 + getRightIndicatorOpacity() * 0.5 }}>
          <span className="text-xs font-medium text-green-600">Biliyorum</span>
          <CheckCircle2 className="w-4 h-4 text-green-500" />
        </div>
      </div>

      {/* Card Stack Effect */}
      <div className="relative h-[320px]">
        {/* Background cards for stack effect */}
        {currentIndex < cards.length - 1 && (
          <>
            <div className="absolute inset-0 bg-white rounded-3xl shadow-md border border-gray-100 transform scale-[0.92] translate-y-4 opacity-60" />
            <div className="absolute inset-0 bg-white rounded-3xl shadow-sm border border-gray-100 transform scale-[0.96] translate-y-2 opacity-80" />
          </>
        )}

        {/* Main Card */}
        <div
          ref={cardRef}
          className="absolute inset-0 cursor-grab active:cursor-grabbing"
          style={getCardStyle()}
          onMouseDown={handleDragStart}
          onMouseMove={handleDragMove}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchMove={handleDragMove}
          onTouchEnd={handleDragEnd}
        >
          {/* Swipe Overlays */}
          <div 
            className="absolute inset-0 bg-gradient-to-r from-red-500/20 to-transparent rounded-3xl z-10 pointer-events-none flex items-center justify-start pl-8"
            style={{ opacity: getLeftIndicatorOpacity() }}
          >
            <div className="bg-red-500 rounded-full p-4">
              <X className="w-8 h-8 text-white" />
            </div>
          </div>
          <div 
            className="absolute inset-0 bg-gradient-to-l from-green-500/20 to-transparent rounded-3xl z-10 pointer-events-none flex items-center justify-end pr-8"
            style={{ opacity: getRightIndicatorOpacity() }}
          >
            <div className="bg-green-500 rounded-full p-4">
              <CheckCircle2 className="w-8 h-8 text-white" />
            </div>
          </div>

          {/* Card Content */}
          <div 
            className="relative h-full perspective-1000"
            onClick={!isDragging ? handleFlip : undefined}
          >
            <div className={cn(
              'absolute inset-0 transition-transform duration-500 transform-style-preserve-3d',
              isFlipped && 'rotate-y-180'
            )}>
              {/* Front */}
              <div className={cn(
                'absolute inset-0 bg-white rounded-3xl shadow-xl border-2 border-gray-100 p-8 flex flex-col items-center justify-center backface-hidden'
              )}>
                <div className="absolute top-4 right-4">
                  <button 
                    onClick={(e) => { e.stopPropagation(); speakText(currentCard?.front); }}
                    className="p-3 hover:bg-blue-50 rounded-full transition-colors"
                  >
                    <Volume2 className="w-6 h-6 text-blue-500" />
                  </button>
                </div>
                
                <div className="text-6xl mb-4">🎴</div>
                <p className="text-3xl font-bold text-gray-800 text-center">{currentCard?.front}</p>
                {currentCard?.phonetic && (
                  <p className="text-gray-400 mt-2 text-lg">{currentCard.phonetic}</p>
                )}
                <p className="text-sm text-gray-400 mt-8 animate-pulse">👆 Çevirmek için dokun</p>
              </div>

              {/* Back */}
              <div className={cn(
                'absolute inset-0 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-3xl shadow-xl p-8 flex flex-col items-center justify-center rotate-y-180 backface-hidden'
              )}>
                <p className="text-4xl font-bold text-white text-center mb-4">{currentCard?.back}</p>
                {currentCard?.example && (
                  <div className="bg-white/20 backdrop-blur rounded-xl p-4 mt-4 max-w-full">
                    <div className="flex items-start gap-2">
                      <Lightbulb className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
                      <p className="text-white/90 text-sm">{currentCard.example}</p>
                    </div>
                  </div>
                )}
                <p className="text-sm text-white/60 mt-6">← Sola: Bilmiyorum | Sağa: Biliyorum →</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons for non-touch */}
      {isFlipped && (
        <div className="flex gap-4 mt-6">
          <Button
            onClick={handleUnknownClick}
            variant="outline"
            className="flex-1 py-6 rounded-2xl border-2 border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 transition-all"
          >
            <X className="w-6 h-6 mr-2" />
            Bilmiyorum
          </Button>
          <Button
            onClick={handleKnownClick}
            className="flex-1 py-6 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 transition-all"
          >
            <CheckCircle2 className="w-6 h-6 mr-2" />
            Biliyorum
          </Button>
        </div>
      )}

      {/* Flip hint when not flipped */}
      {!isFlipped && (
        <div className="text-center mt-6">
          <Button
            onClick={handleFlip}
            className="px-8 py-4 rounded-2xl bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
          >
            🔄 Kartı Çevir
          </Button>
        </div>
      )}

      {/* Stats */}
      <div className="flex justify-center gap-8 mt-6">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-gradient-to-r from-green-400 to-emerald-500" />
          <span className="text-gray-600 font-medium">{knownCards.length} Biliyorum</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-gradient-to-r from-red-400 to-pink-500" />
          <span className="text-gray-600 font-medium">{unknownCards.length} Bilmiyorum</span>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        .perspective-1000 { perspective: 1000px; }
        .transform-style-preserve-3d { transform-style: preserve-3d; }
        .backface-hidden { backface-visibility: hidden; }
        .rotate-y-180 { transform: rotateY(180deg); }
      `}} />
    </div>
  );
}
